import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { db, auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import usePlanFeatures from '../hooks/usePlanFeatures';
import { Building2, User, Lock, Save, Loader2, Eye, EyeOff, Globe, Clock, AlertTriangle } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const IS_SAAS = process.env.REACT_APP_IS_SAAS === 'true';

export default function Settings() {
  const { t } = useTranslation('settings');
  const { user, refreshToken } = useAuth();
  const { planId, isTrial } = usePlanFeatures();
  const isPremiumEligible = isTrial || (planId === 'premium' && user?.subscription?.status === 'active');

  const [businessForm, setBusinessForm] = useState({ name: '' });
  const [profileForm, setProfileForm] = useState({ name: '', surname: '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [domainInput, setDomainInput] = useState('');
  const [savedDomain, setSavedDomain] = useState('');
  const [savingDomain, setSavingDomain] = useState(false);

  useEffect(() => {
    if (!user?.businessId || !user?.uid) return;
    const load = async () => {
      try {
        const bizSnap = await getDoc(doc(db, 'businesses', user.businessId));
        if (bizSnap.exists()) {
          const bizData = bizSnap.data();
          setBusinessForm({ name: bizData.name || '' });
          const cd = bizData.customDomain || '';
          setDomainInput(cd);
          setSavedDomain(cd);
        }
        setProfileForm({
          name: user.name || '',
          surname: user.surname || '',
        });
      } catch (err) {
        toast.error(t('settings.loadError'));
      } finally {
        setLoadingBusiness(false);
      }
    };
    load();
  }, [user?.businessId, user?.uid, user?.name, user?.surname]);

  const handleSaveBusiness = async () => {
    if (!businessForm.name.trim()) {
      toast.error(t('settings.business.nameRequired'));
      return;
    }
    setSavingBusiness(true);
    try {
      await updateDoc(doc(db, 'businesses', user.businessId), {
        name: businessForm.name.trim(),
        updatedAt: serverTimestamp(),
      });
      toast.success(t('settings.business.saveSuccess'));
    } catch (err) {
      toast.error(t('settings.business.saveError') + ': ' + err.message);
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error(t('settings.profile.nameRequired'));
      return;
    }
    setSavingProfile(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        name: profileForm.name.trim(),
        surname: profileForm.surname.trim(),
      });
      await refreshToken();
      toast.success(t('settings.profile.saveSuccess'));
    } catch (err) {
      toast.error(t('settings.profile.saveError') + ': ' + err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('settings.password.allFieldsRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.password.mismatch'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('settings.password.tooShort'));
      return;
    }
    setSavingPassword(true);
    try {
      const currentUser = auth.currentUser;
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success(t('settings.password.success'));
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        toast.error(t('settings.password.wrongPassword'));
      } else {
        toast.error(t('settings.password.updateError') + ': ' + err.message);
      }
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveDomain = async () => {
    setSavingDomain(true);
    try {
      await updateDoc(doc(db, 'businesses', user.businessId), {
        customDomain: domainInput.trim().toLowerCase(),
        customDomainUpdatedAt: serverTimestamp(),
      });
      setSavedDomain(domainInput.trim().toLowerCase());
      toast.success(t('settings.domain.saveSuccess'));
    } catch (err) {
      toast.error(t('settings.domain.saveError') + ': ' + err.message);
    } finally {
      setSavingDomain(false);
    }
  };

  const toggleShow = (field) =>
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  if (loadingBusiness) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* İşletme Bilgileri */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="h-5 w-5 text-blue-500" />
          <h2 className="text-base font-semibold text-gray-800">{t('settings.business.title')}</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.business.nameLabel')}</label>
            <input
              type="text"
              value={businessForm.name}
              onChange={(e) => setBusinessForm({ name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('settings.business.namePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.business.emailLabel')}</label>
            <input
              type="text"
              value={user?.email || ''}
              disabled
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">{t('settings.business.emailReadonly')}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={handleSaveBusiness}
            disabled={savingBusiness}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {savingBusiness ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('settings.business.saveBtn')}
          </button>
        </div>
      </div>

      {/* Profil Bilgileri */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <User className="h-5 w-5 text-blue-500" />
          <h2 className="text-base font-semibold text-gray-800">{t('settings.profile.title')}</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.firstNameLabel')}</label>
            <input
              type="text"
              value={profileForm.name}
              onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.lastNameLabel')}</label>
            <input
              type="text"
              value={profileForm.surname}
              onChange={(e) => setProfileForm((p) => ({ ...p, surname: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('settings.profile.saveBtn')}
          </button>
        </div>
      </div>

      {/* Özel Alan Adı — sadece SaaS modunda */}
      {IS_SAAS && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="h-5 w-5 text-blue-500" />
            <h2 className="text-base font-semibold text-gray-800">{t('settings.domain.title')}</h2>
            <span className="ml-auto text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-0.5 rounded-full">{t('settings.domain.premiumBadge')}</span>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t('settings.domain.subtitle')}</p>

          {!isPremiumEligible && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-700" dangerouslySetInnerHTML={{ __html: t('settings.domain.upgradeWarning') }} />
                <Link to="/billing" className="text-xs text-amber-600 underline mt-0.5 inline-block">
                  {t('settings.domain.upgradeLink')}
                </Link>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.domain.label')}</label>
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('settings.domain.placeholder')}
            />
          </div>

          {isPremiumEligible && savedDomain && (
            <div className="flex items-center gap-2 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Clock className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-700">{t('settings.domain.processingTitle')}</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  <span className="font-mono">{savedDomain}</span> {t('settings.domain.processingDesc')}
                </p>
              </div>
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                {t('settings.domain.pendingBadge')}
              </span>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveDomain}
              disabled={savingDomain}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {savingDomain ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t('settings.domain.saveBtn')}
            </button>
          </div>
        </div>
      )}

      {/* Şifre Değiştir */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock className="h-5 w-5 text-blue-500" />
          <h2 className="text-base font-semibold text-gray-800">{t('settings.password.title')}</h2>
        </div>
        <div className="space-y-4">
          {[
            { field: 'current', labelKey: 'settings.password.currentLabel', key: 'currentPassword' },
            { field: 'new',     labelKey: 'settings.password.newLabel',     key: 'newPassword' },
            { field: 'confirm', labelKey: 'settings.password.confirmLabel', key: 'confirmPassword' },
          ].map(({ field, labelKey, key }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t(labelKey)}</label>
              <div className="relative">
                <input
                  type={showPasswords[field] ? 'text' : 'password'}
                  value={passwordForm[key]}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, [key]: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => toggleShow(field)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={handleChangePassword}
            disabled={savingPassword}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {t('settings.password.updateBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
